import { useCallback } from 'react';
import { Group } from '@mantine/core';
import type { ZodTypeAny } from 'zod';
import { z } from 'zod';
import { DatePicker, TimeInput } from '@mantine/dates';
import { format } from 'date-fns';

import type { BaseInterativeField, FieldDescriptor, StaticFieldProps } from '../../types/common';
import { FieldMeta } from '../common/FieldMeta';

export type APIDateTimeField = BaseInterativeField & {
  type: 'DateTimeField';
  validation?: BaseInterativeField['validation'] & {
    max?: string;
    min?: string;
  };
};

export type DateTimeFieldType = BaseInterativeField & {
  type: 'DateTimeField';
  validation?: BaseInterativeField['validation'] & {
    max?: Date;
    min?: Date;
  };
};

function DateTimeFieldElement({
  field,
  value,
  onValueChange,
  errors,
  isTouched,
}: StaticFieldProps<DateTimeFieldType>) {
  const handleOnChange = useCallback(
    (newValue, type) => {
      onValueChange((prevValue) => {
        const currentDate = new Date((prevValue as Date) ?? new Date());
        const newDate = new Date(newValue);
        const now = new Date();
        const hasInvalidYear = currentDate.getFullYear() < now.getFullYear();
        const hasInvalidMonth =
          currentDate.getFullYear() === now.getFullYear() &&
          currentDate.getMonth() < now.getMonth();
        const hasInvalidDate =
          currentDate.getFullYear() === now.getFullYear() &&
          currentDate.getMonth() === now.getMonth() &&
          currentDate.getDate() < now.getDate();

        if (type === 'date') {
          currentDate.setFullYear(newDate.getFullYear());
          currentDate.setMonth(newDate.getMonth());
          currentDate.setDate(newDate.getDate());
        } else if (type === 'time') {
          currentDate.setHours(newDate.getHours());
          currentDate.setMinutes(newDate.getMinutes());
          currentDate.setSeconds(newDate.getSeconds());
          currentDate.setMilliseconds(newDate.getMilliseconds());
        }

        if (hasInvalidYear) {
          currentDate.setFullYear(now.getFullYear());
          currentDate.setMonth(now.getMonth());
          currentDate.setDate(now.getDate());
        } else if (hasInvalidMonth) {
          currentDate.setMonth(now.getMonth());
          currentDate.setDate(now.getDate());
        } else if (hasInvalidDate) {
          currentDate.setDate(now.getDate());
        }

        return currentDate;
      });
    },
    [onValueChange]
  );

  const handleDateChange = useCallback(
    (newValue: Date) => handleOnChange(newValue, 'date'),
    [handleOnChange]
  );

  const handleTimeChange = useCallback(
    (newValue: Date) => handleOnChange(newValue, 'time'),
    [handleOnChange]
  );

  return (
    <div>
      <FieldMeta field={field} errors={errors} isTouched={isTouched} />
      <Group>
        <DatePicker
          sx={{
            flexGrow: 2,
          }}
          placeholder="What day?"
          onChange={handleDateChange}
          value={value as Date}
          minDate={field.validation?.min}
          maxDate={field.validation?.max}
        />
        <TimeInput
          sx={{
            flexGrow: 1,
          }}
          timePlaceholder="00"
          format="12"
          value={value as Date}
          onChange={handleTimeChange}
        />
      </Group>
    </div>
  );
}

const createValidationSchema = (field: DateTimeFieldType) => {
  let fieldSchema: ZodTypeAny = z.date();

  if (field.validation?.max) {
    // @ts-expect-error Some weird typing
    fieldSchema = fieldSchema.max(
      field.validation.max,
      `Date must be before ${format(field.validation.max, 'MM/dd/yyyy hh:mm a')}`
    );
  }

  if (field.validation?.min) {
    // @ts-expect-error Some weird typing
    fieldSchema = fieldSchema.min(
      field.validation.min,
      `Date must be after ${format(field.validation.min, 'MM/dd/yyyy hh:mm a')}`
    );
  }

  if (field.validation?.isOptional) {
    fieldSchema = fieldSchema.optional();
  }

  return fieldSchema;
};

const Descriptor: FieldDescriptor<APIDateTimeField, DateTimeFieldType> = {
  FieldComponent: DateTimeFieldElement,
  name: 'DateTimeField',
  createValidationSchema,

  // All this gymnastics is done to satisfy the type system :(
  parseFieldResponse({ field, fieldKey }) {
    return [
      {
        key: fieldKey,
        type: 'DateTimeField',
        data: {
          ...(() => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { validation, ...rest } = field;
            return rest;
          })(),
          ...(field.validation
            ? {
                validation: (() => {
                  const { max, min, ...rest } = field.validation;
                  const newValidation: DateTimeFieldType['validation'] = { ...rest };
                  if (max) {
                    newValidation.max = new Date(max);
                  }
                  if (min) {
                    newValidation.min = new Date(min);
                  }

                  return newValidation;
                })(),
              }
            : {}),
        },
      },
    ];
  },
};

export default Descriptor;
